import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import ListingFixerConfig from './ListingFixerConfig';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { EntityLink } from '../../components/shared/EntityLink';
import {
  Zap, Search, FileText, Bot, Shield, Settings, Database,
  Package, CheckCircle2, XCircle,
  Clock, AlertCircle, Eye, Star, Loader2, Activity,
  BarChart3, Sparkles, RefreshCw, X, Power, TrendingUp,
  Tag, Truck, AlertTriangle, MessageSquare, Wrench,
  Globe, ShoppingCart, Megaphone, FolderTree
} from 'lucide-react';

import { useCan, visibleTabs, pickActiveTab, NoAccess, Can } from '../../lib/rbac';
import MarketHealthPanel from './MarketHealthPanel';
import { TrendsDiscoveryConfigPanel } from './TrendsDiscoveryConfigPanel';
import PmScheduleEditor, { pickSchedule, scheduleSummary, type SellerSchedule } from './PmScheduleEditor';

type Tab = 'enricher' | 'automation' | 'processes' | 'markets' | 'directives';

const DRAFT_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  published: 'ok',
  draft: 'info',
  ready: 'info',
  error: 'error',
  closed: 'unknown',
  skipped: 'warning',
  pre_order: 'warning',
  out_of_stock: 'warning',
};

const DECISION_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  auto_applied: 'ok',
  pending: 'warning',
  rejected: 'error',
  error: 'error',
};

export default function AIPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('enricher');

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'enricher', label: t('ai.enricher'), icon: <Sparkles size={16} /> },
    { key: 'automation', label: t('ai.automation'), icon: <Bot size={16} /> },
    { key: 'processes', label: t('auto.title'), icon: <Power size={16} /> },
    { key: 'markets', label: 'Markets', icon: <Globe size={16} /> },
    { key: 'directives', label: t('ai.directives'), icon: <Shield size={16} /> },
  ];

  const allowedTabs = visibleTabs('ai', tabs, can);
  const active = pickActiveTab('ai', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Zap size={24} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">{t('nav.ai')}</h1>
      </div>

      <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {active === 'enricher' && <EnricherTab />}
      {active === 'automation' && <AutomationTab />}
      {active === 'processes' && <AutonomousProcessesTab />}
      {active === 'markets' && <MarketHealthPanel sellerId={useUIStore.getState().sellerId} />}
      {active === 'directives' && <DirectivesTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────
// ENRICHER TAB
// ─────────────────────────────────────────────────

function EnricherTab() {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const { sellerId } = useUIStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('ai-enricher');
  const [selectedDraft, setSelectedDraft] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/ai/enricher/queue?${params}`),
        api.get<any>(`/ai/enricher/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load enricher data:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const statuses = ['all', 'draft', 'ready', 'published', 'error', 'skipped', 'closed', 'pre_order', 'out_of_stock'];

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label={t('ai.totalDrafts')} value={Number(stats.total).toLocaleString()} icon={<FileText size={16} />} color="blue" />
          <StatCard label={t('ai.published')} value={Number(stats.published).toLocaleString()} icon={<CheckCircle2 size={16} />} color="green" />
          <StatCard label={t('ai.drafts')} value={stats.drafts} icon={<FileText size={16} />} color="cyan" />
          <StatCard label={t('ai.errors')} value={stats.errors} icon={<XCircle size={16} />} color="red" />
          <StatCard label={t('ai.needsReview')} value={stats.needs_review} icon={<Eye size={16} />} color="yellow" />
          <StatCard label={t('ai.avgScore')} value={stats.avg_score || '—'} icon={<Star size={16} />} color="purple" />
          <StatCard label={t('ai.last24h')} value={stats.last_24h} icon={<Clock size={16} />} color="emerald" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} título, categoría, ID...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>{s === 'all' ? t('ai.allStatuses') : s.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.product')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">ASIN</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.category')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.score')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.price')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">ML ID</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : data.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedDraft(row)}
                className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {row.product_image ? (
                      <img src={row.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                        <Package size={16} className="text-[var(--color-text-tertiary)]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <span className="text-[var(--color-text)] line-clamp-1 max-w-[250px] block">{row.title_seo || '—'}</span>
                      {row.brand && <span className="text-xs text-[var(--color-text-tertiary)]">{row.brand}</span>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  {row.asin ? <EntityLink type="asin" value={row.asin} /> : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs max-w-[150px] truncate" title={row.ml_category_name}>
                  {row.ml_category_name || '—'}
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={DRAFT_STATUS_MAP[row.status] || 'unknown'} label={row.status} />
                </td>
                <td className="px-3 py-3 text-center">
                  {row.validation_score != null ? (
                    <span className={`font-mono font-medium ${row.validation_score >= 80 ? 'text-green-500' : row.validation_score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {row.validation_score}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text)]">
                  {canMoney && row.price ? `$${Number(row.price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3">
                  {row.ml_item_id ? (
                    <span className="font-mono text-xs text-[var(--color-primary)]">{row.ml_item_id}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs">
                  {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Draft Detail Modal */}
      {selectedDraft && (
        <DraftDetailModal draft={selectedDraft} onClose={() => setSelectedDraft(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
// AUTOMATION TAB
// ─────────────────────────────────────────────────

function AutomationTab() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [configs, setConfigs] = useState<any[]>([]);
  const [decisionStats, setDecisionStats] = useState<any[]>([]);
  const [decisions, setDecisions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [featureFilter, setFeatureFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('ai-automation');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (featureFilter !== 'all') params.set('feature_key', featureFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);

      const [configsRes, statsRes, decisionsRes] = await Promise.all([
        api.get<any[]>(`/ai/automation/configs?seller_id=${sellerId}`),
        api.get<any[]>(`/ai/automation/decisions/stats?seller_id=${sellerId}`),
        api.get<any[]>(`/ai/automation/decisions?${params}`),
      ]);

      if (configsRes.success && configsRes.data) setConfigs(configsRes.data);
      if (statsRes.success && statsRes.data) setDecisionStats(statsRes.data);
      if (decisionsRes.success && decisionsRes.data) {
        setDecisions(decisionsRes.data);
        pag.setTotal((decisionsRes as any).meta?.total || 0);
      }
    } catch (err) {
      console.error('Failed to load automation data:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, featureFilter, statusFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const FEATURE_LABELS: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
    listing_fixer: { icon: <RefreshCw size={16} />, label: 'Listing Fixer', color: 'blue' },
    listing_optimizer: { icon: <Sparkles size={16} />, label: 'Listing Optimizer', color: 'purple' },
    pricing_engine: { icon: <BarChart3 size={16} />, label: 'Pricing Engine', color: 'green' },
    promotion_evaluator: { icon: <Star size={16} />, label: 'Promotion Evaluator', color: 'yellow' },
    claims_defender: { icon: <Shield size={16} />, label: 'Claims Defender', color: 'red' },
    inventory_strategist: { icon: <Database size={16} />, label: 'Inventory Strategist', color: 'cyan' },
    sales_analytics: { icon: <Activity size={16} />, label: 'Sales Analytics', color: 'emerald' },
    trends_discovery: { icon: <Search size={16} />, label: 'Trends Discovery', color: 'orange' },
  };

  // Unique feature keys from configs
  const featureKeys = [...new Set(configs.map(c => c.feature_key))];

  return (
    <div className="space-y-4">
      {/* Feature Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {featureKeys.map(key => {
          const cfg = configs.find(c => c.feature_key === key);
          const info = FEATURE_LABELS[key] || { icon: <Bot size={16} />, label: key, color: 'gray' };
          const stat = decisionStats.find(s => s.feature_key === key);

          return (
            <div key={key} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-${info.color}-500`}>{info.icon}</span>
                  <span className="text-sm font-medium text-[var(--color-text)]">{info.label}</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  cfg?.enabled
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                }`}>
                  {cfg?.enabled ? 'ON' : 'OFF'}
                </span>
              </div>
              {stat ? (
                <div className="grid grid-cols-2 gap-1 text-xs mt-2">
                  <span className="text-[var(--color-text-tertiary)]">{t('ai.totalDecisions')}</span>
                  <span className="text-right text-[var(--color-text)]">{stat.total}</span>
                  <span className="text-[var(--color-text-tertiary)]">{t('ai.autoApplied')}</span>
                  <span className="text-right text-green-500">{stat.auto_applied}</span>
                  <span className="text-[var(--color-text-tertiary)]">{t('ai.pendingReview')}</span>
                  <span className="text-right text-yellow-500">{stat.pending}</span>
                  <span className="text-[var(--color-text-tertiary)]">{t('ai.avgConfidence')}</span>
                  <span className="text-right text-[var(--color-text)]">{stat.avg_confidence}%</span>
                </div>
              ) : (
                <p className="text-xs text-[var(--color-text-tertiary)] mt-2">{t('ai.noDecisions')}</p>
              )}
              {cfg?.last_run_at && (
                <p className="text-xs text-[var(--color-text-tertiary)] mt-2 border-t border-[var(--color-border)] pt-2">
                  {t('ai.lastRun')}: {new Date(cfg.last_run_at).toLocaleString()}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Decisions Table */}
      <div className="flex gap-3 items-center">
        <h3 className="font-medium text-[var(--color-text)]">{t('ai.decisionLog')}</h3>
        <select
          value={featureFilter}
          onChange={(e) => { setFeatureFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="all">{t('ai.allFeatures')}</option>
          {featureKeys.map(k => (
            <option key={k} value={k}>{FEATURE_LABELS[k]?.label || k}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          <option value="all">{t('ai.allStatuses')}</option>
          <option value="auto_applied">Auto-applied</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="error">Error</option>
        </select>
      </div>

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.feature')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.reference')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.status')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.action')}</th>
              <th className="text-center px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('ai.confidence')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : decisions.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : decisions.map((d) => (
              <tr key={d.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-${(FEATURE_LABELS[d.feature_key]?.color || 'gray')}-500`}>
                      {FEATURE_LABELS[d.feature_key]?.icon || <Bot size={14} />}
                    </span>
                    <span className="text-[var(--color-text)]">{FEATURE_LABELS[d.feature_key]?.label || d.feature_key}</span>
                  </div>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-[var(--color-text-secondary)]">
                  {d.reference_id || '—'}
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={DECISION_STATUS_MAP[d.status] || 'unknown'} label={d.status?.replace(/_/g, ' ')} />
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs max-w-[200px] truncate" title={d.action_recommended}>
                  {d.action_recommended || '—'}
                </td>
                <td className="px-3 py-3 text-center">
                  {d.confidence != null ? (
                    <span className={`font-mono font-medium ${d.confidence >= 80 ? 'text-green-500' : d.confidence >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {d.confidence}%
                    </span>
                  ) : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {d.created_at ? new Date(d.created_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────
// AUTONOMOUS PROCESSES TAB
// ─────────────────────────────────────────────────

interface SchedulerProcess {
  feature_key: string;
  enabled: boolean;
  settings: Record<string, unknown>;
  last_run_at: string | null;
  last_run_stats: { status?: string; duration_ms?: number; error?: string } | null;
  registered: boolean;
  running: boolean;
  category: string;
  configurable: Record<string, { type: string; label_es: string; label_en: string; default: unknown; options?: string[] }>;
  default_interval: number | null;
}

interface SchedulerData {
  processes: SchedulerProcess[];
  running: string[];
  registered: string[];
  api_health: Record<string, { ok: boolean; failCount: number }>;
}

const PROCESS_META: Record<string, { icon: React.ReactNode; labelKey: string; color: string; description: string; help?: string }> = {
  listing_fixer: {
    icon: <Wrench size={18} />, labelKey: 'auto.fixer', color: 'blue',
    description: 'Corrige automaticamente items rechazados por ML',
    help: 'Cuando ML pone un item en "under_review" (por fotos, atributos o categoria), este proceso lo detecta, diagnostica la causa, y aplica la correccion automaticamente si tiene confianza alta. Si no puede resolverlo solo, lo deja en la lista "A Revisar" para que lo corrijas manualmente.',
  },
  listing_optimizer: {
    icon: <Sparkles size={18} />, labelKey: 'auto.optimizer', color: 'purple',
    description: 'Sugiere mejoras de titulo, imagenes y atributos para vender mas',
    help: 'Analiza tus listings activos y sugiere optimizaciones: titulos mas atractivos, mejores imagenes, atributos faltantes. Prioriza items que estan perdiendo contra la competencia (losing buy box). Las sugerencias se auto-aplican si superan el umbral de confianza, o quedan pendientes para tu aprobacion.',
  },
  pricing_engine: {
    icon: <BarChart3 size={18} />, labelKey: 'auto.repricing', color: 'green',
    description: 'Ajusta precios para ganar el buy box sin perder margen',
    help: 'Usa el Price-to-Win de ML para saber el precio exacto que necesitas para ganar visibilidad. Compara contra tu costo de Amazon y solo ajusta si el margen se mantiene por encima del minimo configurado. Los items se dividen en 3 tiers por volumen de ventas: los top sellers se revisan mas seguido.',
  },
  price_monitor: {
    icon: <TrendingUp size={18} />, labelKey: 'auto.priceMonitor', color: 'green',
    description: 'Monitorea precios en Amazon y actualiza ML automaticamente',
    help: 'Recorre todos tus items activos, consulta el precio actual en Amazon (via API o Keepa), recalcula el markup, y actualiza el precio en ML. Tambien detecta items sin stock, pre-orders, y delivery lento en Amazon para pausarlos automaticamente.',
  },
  promotion_evaluator: {
    icon: <Tag size={18} />, labelKey: 'auto.promotions', color: 'yellow',
    description: 'Evalua y acepta promociones de ML automaticamente',
    help: 'ML te ofrece promociones periodicamente. Hay 2 tipos principales:\n\n• Co-funded (ML paga parte del descuento): Se auto-aceptan si tus net_proceeds cubren el costo de Amazon. Es plata gratis — ML subsidia el descuento.\n\n• Seller-funded (vos pagas todo): Solo se aceptan si tu margen post-descuento supera el umbral configurado.\n\nLightning Deals y Deal of the Day requieren compromiso de stock — estan desactivados por default.',
  },
  claims_defender: {
    icon: <AlertTriangle size={18} />, labelKey: 'auto.claims', color: 'red',
    description: 'Responde y defiende reclamos de compradores',
    help: 'Cuando un comprador abre un reclamo, este proceso evalua si conviene reembolsar o disputar. Reglas automaticas: si el profit es bajo y es un "no recibido" → reembolsar (no vale la pena pelear). Si el comprador tiene 3+ reclamos previos → disputar (posible fraude). Casos complejos → analisis AI.',
  },
  claims_reconciliation: {
    icon: <Shield size={18} />, labelKey: 'auto.claimsReconciliation', color: 'red',
    description: 'Sincroniza el estado real de reclamos desde ML',
    help: 'ML no siempre envia webhooks cuando un reclamo cambia de estado. Este proceso consulta la API de ML cada hora para detectar cambios que se perdieron: reclamos cerrados, resoluciones a favor, etc. Cuando un reclamo se cierra a favor del comprador, cancela automaticamente la compra en Amazon si aun no se envio.',
  },
  inventory_strategist: {
    icon: <Database size={18} />, labelKey: 'auto.inventoryStrategist', color: 'cyan',
    description: 'Calcula cuando y cuanto reabastecer de cada producto',
    help: 'Analiza la velocidad de venta de cada producto (unidades/dia), calcula cuantos dias de stock quedan, y genera alertas cuando el stock baja del punto de reorden. Ideal para saber que comprar antes de quedarte sin stock.',
  },
  sales_analytics: {
    icon: <Activity size={18} />, labelKey: 'auto.salesAnalytics', color: 'emerald',
    description: 'Clasifica cada item por rendimiento de ventas',
    help: 'Cada corrida analiza los ultimos 30 dias y clasifica cada item en:\n\n• Star: 5+ ventas, creciendo, pocos reclamos\n• Growing: 2+ ventas, tendencia positiva\n• Stable: ventas constantes\n• Declining: ventas cayendo >20%\n• Underperforming: 0 ventas en 30 dias\n\nEsta clasificacion alimenta otros procesos (pricing, ads, optimizer).',
  },
  trends_discovery: {
    icon: <TrendingUp size={18} />, labelKey: 'auto.trendsDiscovery', color: 'orange',
    description: 'Encuentra productos nuevos basado en lo que la gente busca en ML',
    help: 'Consulta la API de ML Trends para saber que esta buscando la gente ahora en cada mercado (Argentina, Mexico, Chile, Colombia, Brasil). Traduce las keywords al ingles, busca productos equivalentes en Amazon via Keepa, y encola los que pasan los filtros de viabilidad (precio, peso, FBA, condicion).\n\nSi "Trends por categoria" esta activado, tambien consulta trends especificos de tus categorias con mas ventas — esto da resultados mucho mas relevantes que los trends generales.',
  },
  order_sync: {
    icon: <Package size={18} />, labelKey: 'auto.orderSync', color: 'orange',
    description: 'Recupera ordenes que ML no notifico por webhook',
    help: 'A veces ML no envia el webhook de una orden nueva. Este proceso consulta la API de ordenes periodicamente para detectar ordenes que se perdieron y procesarlas (trigger de compra en Amazon, labels, etc.).',
  },
  amazon_sync: {
    icon: <Truck size={18} />, labelKey: 'auto.amazonSync', color: 'yellow',
    description: 'Actualiza tracking de envios, reportes y reintentos de compra',
    help: 'Consolida 3 tareas de Amazon:\n1. Tracking: actualiza estado de envio de cada compra via Amazon API\n2. Reports: descarga reportes de Amazon para reconciliar\n3. PO Retry: reintenta compras parciales o detenidas cada 10 min',
  },
  site_items_sync: {
    icon: <Database size={18} />, labelKey: 'auto.siteItemsSync', color: 'blue',
    description: 'Sincroniza el estado real de tus items en cada pais',
    help: 'Recorre todos tus site items en ML (MLA, MLM, MLC, MCO, MLB) y reconcilia el estado local vs el real. Detecta items que cambiaron de status sin webhook (ej: paused → active, o active → under_review). Tambien sincroniza el Price-to-Win para cada item.',
  },
  preorder_checker: {
    icon: <Clock size={18} />, labelKey: 'auto.preorderChecker', color: 'purple',
    description: 'Re-verifica si productos pausados por pre-order u OOS ya estan disponibles',
    help: 'Items que se pausaron porque Amazon los tenia en pre-order o sin stock se re-verifican periodicamente. Si el producto volvio a estar disponible en Amazon (stock normal, FBA, condicion new), se reactiva automaticamente en ML.',
  },
  category_review_recheck: {
    icon: <FolderTree size={18} />, labelKey: 'auto.categoryReviewRecheck', color: 'amber',
    description: 'Re-piensa solo los productos en revisión por categoría: los que alcanzan confianza alta se resuelven sin intervención',
    help: 'Los productos que el cerebro categorizador mandó a revisión (no se animó a decidir solo) se re-piensan periodicamente con dos refuerzos: la memoria de decisiones (tus correcciones y las publicaciones exitosas) y el cerebro que ahora ve tus correcciones previas como ejemplos.\n\nSi la memoria ya conoce el producto, o el cerebro alcanza la confianza minima configurada, la categoria se aplica sola: el producto se re-prepara completo con la categoria nueva y sigue el flujo normal de publicacion (manda la auto-publicacion del vendedor).\n\nLos que siguen dudosos se posponen (hasta 3 intentos) y quedan para resolver a mano en la vista "Revisar por grupos" de Borradores.\n\n• "Confianza minima del cerebro": debajo de eso no decide solo.\n• "Items por corrida": controla el costo de inteligencia artificial por pasada.\n\nNo arranca mientras corre el Monitor de Precios del mismo vendedor.',
  },
  requeue_checker: {
    icon: <RefreshCw size={18} />, labelKey: 'auto.requeueChecker', color: 'purple',
    description: 'Re-chequea los apartados del pipeline (sin stock, sin precio, entrega lenta) y re-encola los que vuelven a cumplir',
    help: 'Los productos del pipeline apartados por motivos transitorios (sin stock en Amazon, sin precio, entrega demasiado lenta) se re-chequean periodicamente con UNA consulta a Amazon cada uno.\n\nSi el producto vuelve a cumplir los requisitos activos (oferta nueva, con stock, precio y entrega dentro del plazo), se re-encola solo y pasa por la preparacion completa — la auto-publicacion del vendedor decide el final.\n\n• "Re-chequear cada (dias)": cada producto que sigue mal espera esos dias hasta el proximo intento.\n• "% de vencidos por corrida": cuanto procesa cada corrida; los que no entraron encabezan la siguiente (prioridad: mas ventas mensuales primero).\n\nSemaforo: no arranca mientras corre el Monitor de Precios del mismo vendedor, y si el Monitor arranca a mitad de corrida, corta limpio y sigue despues. Los apartados permanentes (marca prohibida, duplicados, pausa manual, no-nuevo, datos insuficientes) NO se tocan.',
  },
  carrier_tracking: {
    icon: <Truck size={18} />, labelKey: 'auto.carrierTracking', color: 'cyan',
    description: 'Actualiza tracking de paquetes via KD100',
    help: 'Para envios que no son de Amazon (tracking manual), consulta KD100 cada 4 horas para actualizar el estado: en transito, entregado, con problema, etc.',
  },
  amazon_po_retry: {
    icon: <RefreshCw size={18} />, labelKey: 'auto.amazonPORetry', color: 'yellow',
    description: 'Reintenta compras de Amazon que fallaron o quedaron parciales',
    help: 'Cuando una Purchase Order en Amazon queda en estado "partial" o "stopped" (por stock, precio, o error de API), este proceso la reintenta automaticamente. Tambien detecta compras huerfanas (ordenes sin PO) y las reintenta.',
  },
  ptw_pricing: {
    icon: <TrendingUp size={18} />, labelKey: 'auto.pricingEngine', color: 'green',
    description: 'Pricing competitivo con Price-to-Win de ML',
    help: 'Variante del pricing engine que usa exclusivamente el Price-to-Win de ML para determinar el precio optimo.',
  },
  smart_alerts: {
    icon: <AlertTriangle size={18} />, labelKey: 'auto.smartAlerts', color: 'red',
    description: 'Te avisa cuando algo anda mal: margenes, claims, stock, pipeline',
    help: 'Monitorea 7 condiciones criticas y dispara alertas:\n\n• Ordenes a perdida (margen negativo en 24h)\n• Cancel rate alto (>20% en 24h)\n• Muchos items en revision (>5% del catalogo)\n• Tokens de Keepa agotandose\n• Pipeline de discovery detenido (0 publicaciones/dia)\n• Reclamos sin responder >24h\n• Compras Amazon trabadas >2h\n\nSi activas Telegram, las alertas llegan directo a tu celular. Respeta "horario silencioso" (12am-6am por default).',
  },
  action_tracker: {
    icon: <CheckCircle2 size={18} />, labelKey: 'auto.actionTracker', color: 'cyan',
    description: 'Verifica que las acciones automaticas se ejecutaron correctamente',
    help: 'Despues de que el sistema ejecuta una accion (cambio de precio, aceptar promo, etc.), este proceso verifica via ML API que el cambio realmente se aplico. Si no se aplico, lo marca para reintentar.',
  },
  batch_intelligence: {
    icon: <Package size={18} />, labelKey: 'auto.batchIntelligence', color: 'purple',
    description: 'Analiza operaciones en lote y detecta problemas',
    help: 'Monitorea las operaciones bulk (pause/activate/reprice masivos) y verifica que se completaron correctamente. Detecta items con delivery lento post-release y alerta.',
  },
  product_ads: {
    icon: <Megaphone size={18} />, labelKey: 'auto.productAds', color: 'green',
    description: 'Publicidad pagada en ML — tus items aparecen primero en busquedas',
    help: 'Product Ads es el sistema de publicidad PPC (pago por click) de MercadoLibre. Tus items aparecen en posiciones destacadas en los resultados de busqueda.\n\nEste proceso sincroniza tus campanas y metricas (impresiones, clicks, costo, ventas, ROAS). Puede auto-pausar ads que gastan sin convertir.\n\nPrerequisito: Hay que activar Product Ads desde el panel de ML primero (MercadoLibre → Publicidad). Despues configura aqui el budget y la estrategia.\n\n• Profitability: menos anuncios, pero a compradores mas probables. Ideal para productos que ya venden bien.\n• Increase: balance entre alcance y rentabilidad.\n• Visibility: maximo alcance. Ideal para productos nuevos.\n\nVe las metricas detalladas en MercadoLibre → tab Ads.',
  },
  bestsellers_discovery: {
    icon: <ShoppingCart size={18} />, labelKey: 'auto.bestsellersDiscovery', color: 'orange',
    description: 'Descubre los productos mas vendidos de Amazon y los encola para publicar',
    help: 'Recorre las listas de bestsellers de Amazon por categoria (Electronics, Tools, Toys, Health, Books) via Keepa. Filtra los que ya tenes en tu catalogo y encola los nuevos para el pipeline de enriquecimiento → publicacion.\n\nEs complementario a los Scouts (que buscan productos NUEVOS listados recientemente) y a Trends (que busca lo que la gente busca en ML). Bestsellers busca los productos que MAS SE VENDEN en Amazon — alta demanda comprobada.\n\nCorre cada 24 horas. Las categorias se pueden configurar.',
  },
  visits_tracker: {
    icon: <Eye size={18} />, labelKey: 'auto.visitsTracker', color: 'cyan',
    description: 'Mide cuantas visitas recibe cada item y detecta problemas de conversion',
    help: 'Consulta la API de ML para saber cuantas visitas (views) recibio cada item en los ultimos 7 dias. Cruza con las ventas para clasificar:\n\n• Muchas visitas + 0 ventas = problema de precio, fotos o descripcion\n• Pocas visitas + buena conversion = candidato a publicidad (Product Ads)\n• Buena conversion = dejarlo como esta\n\nNota: Actualmente ML no reporta visitas para items CBT parent — solo para site items. Esta funcion puede dar resultados limitados hasta que se ajuste para consultar site items.',
  },
  market_health: {
    icon: <Globe size={18} />, labelKey: 'auto.marketHealth', color: 'emerald',
    description: 'Radiografia de cada mercado: que vende, que no, donde hay oportunidad',
    help: 'Genera un reporte por pais (Argentina, Mexico, Chile, Colombia, Brasil) con:\n\n• Items activos, pausados y en revision\n• Ordenes y revenue de los ultimos 30 dias\n• Ticket promedio (AOV)\n• Tasa de conversion (ordenes / items activos)\n• Top 5 categorias que mas venden\n• Keywords trending del momento\n\nUsa este reporte para identificar mercados dormidos (items activos pero 0 ventas) y decidir donde enfocar esfuerzos. Ve los resultados en AI → tab Markets.',
  },
  ml_dimension_audit: {
    icon: <Package size={18} />, labelKey: 'auto.dimensionAudit', color: 'orange',
    description: 'Revisa todos los días que el peso de los avisos coincida con lo que Mercado Libre cobra de envío',
    help: 'Mercado Libre pesa cada paquete en su centro de envíos. Si pesa más que lo declarado en el aviso, nos cobra la diferencia de envío aparte, y el aviso sigue con el peso viejo: el comprador paga un precio calculado con el envío barato y la diferencia sale de nuestra ganancia.\n\nUna vez por día (a la hora configurada, hora de Miami) este proceso:\n\n• Revisa las ventas de las últimas 24 horas y las ventas todavía no entregadas de los días anteriores (el cobro extra aparece recién cuando el paquete pasa por el centro de Mercado Libre).\n• Anota el cobro extra en las cuentas de esa venta.\n• Calcula el peso real con la calculadora de envíos de Mercado Libre y lo corrige en el aviso de todos los países. Mercado Libre entonces sube el precio al comprador y lo que nos queda vuelve a ser lo planeado.\n\nNo corrige solo: envíos con varios productos distintos, avisos con variantes, o pesos que darían más de 4 veces el declarado. Esos quedan para revisar a mano.',
  },
};

const CATEGORY_ORDER = ['critical', 'sync', 'automation'];

export function AutonomousProcessesTab() {
  const { t, i18n } = useTranslation();
  const can = useCan();
  const canToggle = can('ai.toggle_run');
  const { sellerId } = useUIStore();
  const [data, setData] = useState<SchedulerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editSettings, setEditSettings] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [runningManual, setRunningManual] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [autoPurchase, setAutoPurchase] = useState(false);
  const [maxProfitLoss, setMaxProfitLoss] = useState<number | null>(null);
  const [purchaseDelay, setPurchaseDelay] = useState<number>(0);
  const [loadingAP, setLoadingAP] = useState(true);
  // Horario de la pasada completa del Monitor (director de turnos) para la fila del proceso.
  const [pmSchedule, setPmSchedule] = useState<SellerSchedule | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadData = useCallback(async () => {
    try {
      const res = await api.get<SchedulerData>(`/scheduler/status?seller_id=${sellerId}`);
      if (res.success && res.data) setData(res.data as unknown as SchedulerData);
    } catch (err) {
      console.error('Failed to load scheduler status:', err);
    } finally {
      setLoading(false);
    }
    try {
      const c = await api.get<any>('/price-monitor/conductor');
      setPmSchedule(c.success && c.data ? pickSchedule(c.data as any, sellerId) : null);
    } catch {
      setPmSchedule(null);
    }
  }, [sellerId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Auto-purchase config
  useEffect(() => {
    setLoadingAP(true);
    api.get<any>(`/amazon/config/purchase-options?seller_id=${sellerId}`)
      .then(r => {
        setAutoPurchase(r.data?.auto_purchase ?? false);
        setMaxProfitLoss(r.data?.max_profit_loss ?? null);
        setPurchaseDelay(r.data?.auto_purchase_delay_minutes ?? 0);
      })
      .catch(() => {})
      .finally(() => setLoadingAP(false));
  }, [sellerId]);

  const toggleAutoPurchase = async () => {
    const newVal = !autoPurchase;
    setAutoPurchase(newVal);
    try {
      await api.put('/amazon/config/purchase-options', { seller_id: sellerId, auto_purchase: newVal });
    } catch {
      setAutoPurchase(!newVal);
    }
  };

  const handleToggle = async (featureKey: string) => {
    setToggling(featureKey);
    try {
      await api.put(`/ai/automation/${featureKey}/toggle`, { seller_id: sellerId });
      await loadData();
    } catch { /* ignore */ }
    setToggling(null);
  };

  const handleExpand = (key: string) => {
    if (expanded === key) {
      setExpanded(null);
    } else {
      setExpanded(key);
      const proc = data?.processes.find(p => p.feature_key === key);
      setEditSettings(proc?.settings || {});
    }
  };

  const handleSaveSettings = async (featureKey: string) => {
    setSaving(true);
    const res = await api.put(`/scheduler/${featureKey}/settings?seller_id=${sellerId}`, { settings: editSettings });
    setSaving(false);
    if (res.success) {
      showToast(t('auto.settingsSaved'), true);
      loadData();
    }
  };

  const handleRunNow = async (featureKey: string) => {
    setRunningManual(featureKey);
    const res = await api.post(`/scheduler/${featureKey}/run?seller_id=${sellerId}`, {});
    if (res.success) {
      showToast(`${featureKey} ${t('auto.running')}`, true);
      setTimeout(loadData, 2000);
    } else {
      showToast((res as any).error || 'Error', false);
    }
    setRunningManual(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-[var(--color-text-tertiary)] py-8 text-center">{t('common.noData')}</p>;
  }

  const processes = data.processes || [];
  const isES = i18n.language === 'es';

  function formatTimeAgo(dateStr: string | null): string {
    if (!dateStr) return t('auto.neverRun');
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }

  function formatDuration(ms: number | undefined): string {
    if (!ms) return '';
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}m`;
  }

  // Group by category
  const grouped = CATEGORY_ORDER.map(cat => ({
    category: cat,
    label: t(`auto.${cat}`),
    items: processes.filter(p => (p.category || p.settings?.category) === cat),
  })).filter(g => g.items.length > 0);

  // Uncategorized
  const categorized = new Set(grouped.flatMap(g => g.items.map(i => i.feature_key)));
  const uncategorized = processes.filter(p => !categorized.has(p.feature_key));
  if (uncategorized.length) grouped.push({ category: 'other', label: 'Otros', items: uncategorized });

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-sm font-medium shadow-lg ${
          toast.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[var(--color-text)]">{t('auto.title')}</h2>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
            {processes.filter(p => p.enabled).length} / {processes.length} {t('auto.enabled').toLowerCase()}
            {data.running.length > 0 && (
              <span className="ml-2 text-blue-400">· {data.running.length} {t('auto.running').toLowerCase()}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => { setLoading(true); loadData(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] rounded-lg border border-[var(--color-border)]"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Auto-Purchase Toggle — compra automática y su límite de pérdida */}
      <Can cap="ai.auto_purchase_config">
      <div className="p-4 rounded-lg border border-purple-500/30 bg-purple-500/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart size={20} className="text-purple-500" />
            <div>
              <h4 className="text-sm font-medium text-[var(--color-text)]">{t('settings.autoPurchaseTitle')}</h4>
              <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{t('settings.autoPurchaseDesc')}</p>
            </div>
          </div>
          <button
            onClick={toggleAutoPurchase}
            disabled={loadingAP}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              autoPurchase ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              autoPurchase ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
        <p className={`text-xs mt-2 ${autoPurchase ? 'text-purple-400' : 'text-[var(--color-text-secondary)]'}`}>
          {autoPurchase ? t('settings.autoPurchaseOn') : t('settings.autoPurchaseOff')}
        </p>
        {autoPurchase && (
          <div className="mt-3 pt-3 border-t border-purple-500/20">
            <label className="text-xs text-[var(--color-text-secondary)] block mb-1.5">
              {t('settings.maxProfitLossLabel')}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--color-text-secondary)]">$</span>
              <input
                type="number"
                min="0"
                step="0.5"
                placeholder={t('settings.maxProfitLossPlaceholder')}
                value={maxProfitLoss ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : parseFloat(e.target.value);
                  setMaxProfitLoss(val);
                }}
                onBlur={async () => {
                  try {
                    await api.put('/amazon/config/purchase-options', { seller_id: sellerId, max_profit_loss: maxProfitLoss });
                  } catch { /* ignore */ }
                }}
                className="w-24 px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:border-purple-500 focus:outline-none"
              />
              <span className="text-xs text-[var(--color-text-tertiary)]">USD</span>
            </div>
            <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
              {t('settings.maxProfitLossHint')}
            </p>
            <label className="text-xs text-[var(--color-text-secondary)] block mb-1.5 mt-3">
              {t('settings.purchaseDelayLabel')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="5"
                placeholder={t('settings.purchaseDelayPlaceholder')}
                value={purchaseDelay === 0 ? '' : purchaseDelay}
                onChange={(e) => {
                  const val = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
                  setPurchaseDelay(val);
                }}
                onBlur={async () => {
                  try {
                    await api.put('/amazon/config/purchase-options', { seller_id: sellerId, auto_purchase_delay_minutes: purchaseDelay });
                  } catch { /* ignore */ }
                }}
                className="w-24 px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:border-purple-500 focus:outline-none"
              />
              <span className="text-xs text-[var(--color-text-tertiary)]">min</span>
            </div>
            <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
              {t('settings.purchaseDelayHint')}
            </p>
          </div>
        )}
      </div>
      </Can>

      {/* API Health */}
      {Object.keys(data.api_health).length > 0 && (
        <div className="flex gap-3 text-xs">
          {Object.entries(data.api_health).map(([svc, h]) => (
            <span key={svc} className={`flex items-center gap-1 ${h.ok ? 'text-green-500' : 'text-red-400'}`}>
              <span className={`w-2 h-2 rounded-full ${h.ok ? 'bg-green-500' : 'bg-red-500 animate-pulse'}`} />
              {svc}
            </span>
          ))}
        </div>
      )}

      {/* Grouped processes */}
      {grouped.map(group => (
        <div key={group.category} className="space-y-1">
          <h3 className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider px-1">
            {group.label}
          </h3>
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
            <div className="divide-y divide-[var(--color-border)]">
              {group.items.map((proc) => {
                const meta = PROCESS_META[proc.feature_key] || {
                  icon: <Bot size={18} />,
                  labelKey: proc.feature_key,
                  color: 'gray',
                  description: proc.feature_key,
                };
                const isToggling = toggling === proc.feature_key;
                const isExpanded = expanded === proc.feature_key;
                const lastStats = proc.last_run_stats;
                const intervalMin = (proc.settings?.interval_minutes as number) || proc.default_interval || 60;
                // El Monitor de Precios no corre por intervalo: lo manda el director de turnos
                // (horas fijas por empresa, hora de Miami) → mostrar el horario, no los minutos.
                const pmSummary = proc.feature_key === 'price_monitor' ? scheduleSummary(pmSchedule) : null;

                return (
                  <div key={proc.feature_key}>
                    <div
                      className={`flex items-center gap-4 px-4 py-3 hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer ${
                        !proc.enabled ? 'opacity-50' : ''
                      }`}
                      onClick={() => handleExpand(proc.feature_key)}
                    >
                      {/* Icon */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-${meta.color}-500/10`}>
                        <span className={`text-${meta.color}-500`}>{meta.icon}</span>
                      </div>

                      {/* Name + Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-[var(--color-text)]">{t(meta.labelKey)}</p>
                          {proc.running && (
                            <span className="flex items-center gap-1 text-[10px] text-blue-400">
                              <Loader2 size={10} className="animate-spin" /> {t('auto.running')}
                            </span>
                          )}
                          {!proc.registered && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400">
                              {t('auto.notRegistered')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[var(--color-text-tertiary)]">
                          <span>{meta.description}</span>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="hidden md:flex items-center gap-4 text-xs shrink-0">
                        {pmSummary ? (
                          <span className="text-[var(--color-text-muted)]" title="Horario de la pasada completa (hora de Miami)">{pmSummary}</span>
                        ) : (
                          <span className="text-[var(--color-text-muted)] font-mono">{intervalMin}m</span>
                        )}
                        {lastStats?.status && (
                          <span className={lastStats.status === 'ok' ? 'text-green-500' : 'text-red-400'}>
                            {lastStats.status === 'ok' ? '✓' : '✗'} {formatDuration(lastStats.duration_ms)}
                          </span>
                        )}
                        <span className="text-[var(--color-text-tertiary)] w-12 text-right">
                          {formatTimeAgo(proc.last_run_at)}
                        </span>
                      </div>

                      {/* Toggle */}
                      {canToggle && <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(proc.feature_key); }}
                        disabled={isToggling}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
                          proc.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                        } ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          proc.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>}
                    </div>

                    {/* Expanded settings panel */}
                    {isExpanded && proc.feature_key === 'price_monitor' ? (
                      <PriceMonitorConfigPanel
                        sellerId={sellerId}
                        proc={proc}
                        lastStats={lastStats}
                        formatDuration={formatDuration}
                        onRunNow={() => handleRunNow(proc.feature_key)}
                        running={proc.running || runningManual === proc.feature_key}
                        showToast={showToast}
                        onScheduleSaved={loadData}
                      />
                    ) : isExpanded && proc.feature_key === 'trends_discovery' ? (
                      <TrendsDiscoveryConfigPanel
                        sellerId={sellerId}
                        proc={proc}
                        lastStats={lastStats}
                        formatDuration={formatDuration}
                        onRunNow={() => handleRunNow(proc.feature_key)}
                        running={proc.running || runningManual === proc.feature_key}
                        showToast={showToast}
                      />
                    ) : isExpanded && proc.feature_key === 'listing_fixer' ? (
                      <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
                        {meta.help && (
                          <div className="mb-4 p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)]">
                            <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5 flex items-center gap-1.5">
                              <AlertCircle size={12} /> Como funciona
                            </p>
                            <div className="text-xs text-[var(--color-text-tertiary)] leading-relaxed whitespace-pre-line">
                              {meta.help}
                            </div>
                          </div>
                        )}
                        <ListingFixerConfig />
                      </div>
                    ) : isExpanded && (
                      <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
                        {/* Help text */}
                        {meta.help && (
                          <div className="mb-4 p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)]">
                            <p className="text-xs font-semibold text-[var(--color-text-secondary)] mb-1.5 flex items-center gap-1.5">
                              <AlertCircle size={12} /> Como funciona
                            </p>
                            <div className="text-xs text-[var(--color-text-tertiary)] leading-relaxed whitespace-pre-line">
                              {meta.help}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Interval */}
                          <div>
                            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                              {t('auto.interval')}
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={(editSettings.interval_minutes as number) || intervalMin}
                              onChange={e => setEditSettings(s => ({ ...s, interval_minutes: parseInt(e.target.value) || intervalMin }))}
                              className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                            />
                          </div>

                          {/* Configurable fields */}
                          {Object.entries(proc.configurable || {}).map(([fieldKey, field]) => (
                            <div key={fieldKey}>
                              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                                {isES ? field.label_es : field.label_en}
                              </label>
                              {field.type === 'boolean' ? (
                                <button
                                  onClick={() => setEditSettings(s => ({ ...s, [fieldKey]: !s[fieldKey] && s[fieldKey] !== undefined ? false : !(field.default as boolean) }))}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    (editSettings[fieldKey] !== undefined ? editSettings[fieldKey] : field.default) ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                                  }`}
                                >
                                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    (editSettings[fieldKey] !== undefined ? editSettings[fieldKey] : field.default) ? 'translate-x-6' : 'translate-x-1'
                                  }`} />
                                </button>
                              ) : field.type === 'select' ? (
                                <select
                                  value={String(editSettings[fieldKey] ?? field.default)}
                                  onChange={e => setEditSettings(s => ({ ...s, [fieldKey]: e.target.value }))}
                                  className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                                >
                                  {(field.options || []).map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="number"
                                  value={String(editSettings[fieldKey] ?? field.default ?? '')}
                                  onChange={e => setEditSettings(s => ({ ...s, [fieldKey]: parseFloat(e.target.value) || 0 }))}
                                  className="w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm"
                                />
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Last run info */}
                        {lastStats && (
                          <div className="mt-3 text-xs text-[var(--color-text-muted)]">
                            {t('auto.lastResult')}: <span className={lastStats.status === 'ok' ? 'text-green-500' : 'text-red-400'}>{lastStats.status}</span>
                            {lastStats.duration_ms && <span> · {t('auto.duration')}: {formatDuration(lastStats.duration_ms)}</span>}
                            {lastStats.error && <span className="text-red-400"> · {lastStats.error}</span>}
                            {/* Contadores propios del servicio (ej. resolutor de categorías, cerebro categorizador) */}
                            {Object.entries(lastStats as Record<string, unknown>)
                              .filter(([k, v]) => !['status', 'duration_ms', 'error', 'finished_at', 'date'].includes(k) && v !== null && v !== undefined && typeof v !== 'object')
                              .map(([k, v]) => (
                                <span key={k} className="inline-block ml-2 px-1.5 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]">
                                  {k.replace(/_/g, ' ')}: <b>{String(v)}</b>
                                </span>
                              ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-4">
                          {canToggle && <button
                            onClick={() => handleSaveSettings(proc.feature_key)}
                            disabled={saving}
                            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
                          >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : t('auto.saveSettings')}
                          </button>}
                          {canToggle && proc.registered && (
                            <button
                              onClick={() => handleRunNow(proc.feature_key)}
                              disabled={proc.running || runningManual === proc.feature_key}
                              className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {proc.running || runningManual === proc.feature_key
                                ? <Loader2 size={14} className="animate-spin" />
                                : <Zap size={14} />}
                              {t('auto.runNow')}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────
// PRICE MONITOR CONFIG PANEL (inside AutonomousProcessesTab)
// ─────────────────────────────────────────────────

interface PMConfigPanelProps {
  sellerId: number;
  proc: any;
  lastStats: any;
  formatDuration: (ms: number | undefined) => string;
  onRunNow: () => void;
  running: boolean;
  showToast: (msg: string, ok: boolean) => void;
  onScheduleSaved?: () => void;
}

function PriceMonitorConfigPanel({ sellerId, proc, lastStats, formatDuration, onRunNow, running, showToast, onScheduleSaved }: PMConfigPanelProps) {
  const { t } = useTranslation();
  const can = useCan();
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [lastRun, setLastRun] = useState<any>(null);
  const [showRunErrors, setShowRunErrors] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<any>(`/amazon/price-monitor/config?seller_id=${sellerId}`);
        if (res.success && res.data) {
          setConfig(res.data);
          setEdits(res.data);
        }
        const lr = await api.get<any>(`/price-monitor/last-run?seller_id=${sellerId}`);
        if (lr.success && lr.data) setLastRun(lr.data);
      } catch (err) {
        console.error('Failed to load PM config:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [sellerId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/amazon/price-monitor/config?seller_id=${sellerId}`, edits);
      if (res.success) {
        setConfig(res.data as any);
        setEdits(res.data as any);
        showToast(t('auto.pmConfigSaved'), true);
      } else {
        showToast((res as any).error || t('auto.pmConfigError'), false);
      }
    } catch {
      showToast(t('auto.pmConfigError'), false);
    }
    setSaving(false);
  };

  const update = (field: string, value: any) => setEdits(e => ({ ...e, [field]: value }));

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm';
  const labelCls = 'block text-xs font-medium text-[var(--color-text-secondary)] mb-1';
  const helpCls = 'text-[10px] text-[var(--color-text-tertiary)] mt-0.5';

  const ToggleField = ({ field, label }: { field: string; label: string }) => (
    <div className="flex items-center justify-between">
      <label className="text-xs font-medium text-[var(--color-text-secondary)]">{label}</label>
      <button
        onClick={() => update(field, !edits[field])}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          edits[field] ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          edits[field] ? 'translate-x-6' : 'translate-x-1'
        }`} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="px-4 py-6 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)] flex justify-center">
        <Loader2 size={20} className="animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
        <p className="text-sm text-red-400">{t('auto.pmConfigError')}</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 bg-[var(--color-bg-tertiary)] border-t border-[var(--color-border)]">
      <h4 className="text-xs font-semibold text-[var(--color-text-tertiary)] uppercase tracking-wider mb-3">
        {t('auto.pmConfig')}
      </h4>

      {/* Horario de la pasada completa (2026-10-12): días + horas fijas en hora de Miami, lo manda el
          director de turnos. Reemplaza los campos viejos "intervalo (h)" y "hora UTC", que ningún
          proceso leía. Se guarda aparte (botón propio), no con el "Guardar" de abajo. */}
      <PmScheduleEditor
        sellerId={sellerId}
        canEdit={can('ai.price_monitor_config')}
        processEnabled={!!proc.enabled}
        showToast={showToast}
        onSaved={onScheduleSaved}
      />

      {/* Timing Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>{t('auto.pmNotifyLevel')}</label>
          <select
            value={edits.notify_level ?? 'important'}
            onChange={e => update('notify_level', e.target.value)}
            className={inputCls}
          >
            <option value="all">All</option>
            <option value="important">Important</option>
            <option value="errors">Errors only</option>
            <option value="none">None</option>
          </select>
        </div>
      </div>

      {/* Price Thresholds */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>{t('auto.pmPriceThreshold')}</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={edits.price_threshold_usd ?? 0.50}
            onChange={e => update('price_threshold_usd', parseFloat(e.target.value) || 0)}
            className={inputCls}
          />
          <p className={helpCls}>{t('auto.pmPriceThresholdHelp')}</p>
        </div>
        <div>
          <label className={labelCls}>{t('auto.pmPriceCap')}</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={edits.price_cap_max_usd ?? ''}
            onChange={e => update('price_cap_max_usd', e.target.value === '' ? null : parseFloat(e.target.value))}
            className={inputCls}
            placeholder="Sin limite"
          />
          <p className={helpCls}>{t('auto.pmPriceCapHelp')}</p>
        </div>
        <div>
          <label className={labelCls}>{t('auto.pmPriceFloor')}</label>
          <input
            type="number"
            step="0.01"
            min={0}
            value={edits.price_floor_min_usd ?? ''}
            onChange={e => update('price_floor_min_usd', e.target.value === '' ? null : parseFloat(e.target.value))}
            className={inputCls}
            placeholder="Sin limite"
          />
          <p className={helpCls}>{t('auto.pmPriceFloorHelp')}</p>
        </div>
      </div>

      {/* Toggles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <ToggleField field="auto_pause_no_stock" label={t('auto.pmAutoPauseNoStock')} />
        <ToggleField field="auto_pause_no_fba" label={t('auto.pmAutoPauseNoFba')} />
        <ToggleField field="auto_reactivate_on_restock" label={t('auto.pmAutoReactivate')} />
      </div>

      {/* Offer Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <ToggleField field="require_prime" label="Require Prime" />
        <ToggleField field="prefer_fba" label="Prefer FBA" />
        <div>
          <label className={labelCls}>Offer Condition</label>
          <select
            value={edits.offer_condition ?? 'new'}
            onChange={e => update('offer_condition', e.target.value)}
            className={inputCls}
          >
            <option value="new">New</option>
            <option value="used">Used</option>
            <option value="refurbished">Refurbished</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Delivery Stability</label>
          <input
            type="number"
            min={1}
            max={10}
            value={edits.min_delivery_stability ?? 1}
            onChange={e => update('min_delivery_stability', parseInt(e.target.value) || 1)}
            className={inputCls}
          />
          <p className={helpCls}>Runs consecutivos con delivery OK antes de reactivar</p>
        </div>
      </div>

      {/* Recheck days */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>{t('auto.pmPreorderRecheck')}</label>
          <input
            type="number"
            min={1}
            max={30}
            value={edits.preorder_recheck_days ?? 5}
            onChange={e => update('preorder_recheck_days', parseInt(e.target.value) || 5)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t('auto.pmOosRecheck')}</label>
          <input
            type="number"
            min={1}
            max={30}
            value={edits.oos_recheck_days ?? 3}
            onChange={e => update('oos_recheck_days', parseInt(e.target.value) || 3)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t('auto.pmMaxDelivery')}</label>
          <input
            type="number"
            min={1}
            max={60}
            value={edits.max_delivery_days ?? ''}
            onChange={e => update('max_delivery_days', e.target.value === '' ? null : parseInt(e.target.value))}
            className={inputCls}
            placeholder="Sin limite"
          />
          <p className={helpCls}>{t('auto.pmMaxDeliveryHelp')}</p>
        </div>
      </div>

      {/* Recuperación avanzada de pausados (2026-05) */}
      <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
        <p className={labelCls}>Recuperación avanzada de pausados</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <ToggleField field="watch_verify_full_offers" label="Verificar todas las ofertas" />
            <p className={helpCls}>
              En los productos en vigilancia, mira la lista completa de ofertas de Amazon (no solo
              una) y elige la que llega a tiempo, igual que el comprador. Recupera productos
              apagados que sí se pueden surtir.
            </p>
          </div>
          <div>
            <ToggleField field="unfreeze_clean_without_reason" label="Descongelar sanos sin motivo" />
            <p className={helpCls}>
              Descongela productos que quedaron trabados sin motivo registrado cuando vuelven a
              cumplir todo (stock + entrega), en vez de esperar el plazo de 7 días.
            </p>
          </div>
          <div>
            <ToggleField field="reconcile_ml_truth" label="Reconciliar estado real con Mercado Libre" />
            <p className={helpCls}>
              Cada hora compara lo que creemos (activo) contra lo que Mercado Libre tiene de verdad
              y corrige los productos que figuran activos pero en realidad están apagados: los
              reactiva (si hay stock) o corrige nuestro registro. Evita perder ventas por productos
              apagados sin que lo sepamos.
            </p>
          </div>
        </div>
      </div>

      {/* Resumen última corrida (2026-05-29) */}
      {lastRun?.run && (
        <div className="mb-4 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
          <p className={labelCls}>
            Última corrida del monitor
            {lastRun.run.process_type === 'price_monitor_watch_refetch'
              ? ' (vigilancia)'
              : lastRun.run.label
                ? ` (${lastRun.run.label})`
                : ' (completa)'}
            {lastRun.run.started_at && (
              <span className="font-normal text-[var(--color-text-tertiary)]">
                {' · '}{new Date(lastRun.run.started_at).toLocaleString()}
              </span>
            )}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mt-1">
            <span>Revisados: <b>{lastRun.run.stats?.fetched ?? lastRun.run.stats?.asins ?? 0}</b></span>
            <span className="text-green-500">Reactivados: <b>{lastRun.run.stats?.reactivated ?? 0}</b></span>
            <span className="text-yellow-500">Pausados: <b>{lastRun.run.stats?.paused ?? 0}</b></span>
            <span>Repreciados: <b>{lastRun.run.stats?.repriced ?? 0}</b></span>
            <span className={(lastRun.run.stats?.errors ?? 0) > 0 ? 'text-red-400' : 'text-[var(--color-text-tertiary)]'}>
              Con error: <b>{lastRun.run.stats?.errors ?? 0}</b>
            </span>
          </div>
          {lastRun.errors?.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowRunErrors(s => !s)}
                className="text-xs text-red-400 underline"
              >
                {showRunErrors ? 'Ocultar' : `Ver ${lastRun.errors.length} producto(s) que fallaron`}
              </button>
              {showRunErrors && (
                <div className="mt-2 max-h-52 overflow-auto text-[11px] space-y-1 pr-1">
                  {lastRun.errors.map((e: any, i: number) => (
                    <div key={i} className="border-b border-[var(--color-border)] pb-1">
                      <div className="text-[var(--color-text)]">{e.title || e.item_id}</div>
                      <div className="text-red-400">{e.reason}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Last run info */}
      {lastStats && (
        <div className="text-xs text-[var(--color-text-muted)] mb-3">
          {t('auto.lastResult')}: <span className={lastStats.status === 'ok' ? 'text-green-500' : 'text-red-400'}>{lastStats.status}</span>
          {lastStats.duration_ms && <span> · {t('auto.duration')}: {formatDuration(lastStats.duration_ms)}</span>}
          {lastStats.error && <span className="text-red-400"> · {lastStats.error}</span>}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {can('ai.price_monitor_config') && <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : t('auto.saveSettings')}
        </button>}
        {can('ai.toggle_run') && proc.registered && (
          <button
            onClick={onRunNow}
            disabled={running}
            className="px-4 py-2 text-sm rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-50 flex items-center gap-1.5"
          >
            {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {t('auto.runNow')}
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// DIRECTIVES TAB
// ─────────────────────────────────────────────────

function DirectivesTab() {
  const { t } = useTranslation();
  const [directives, setDirectives] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.get<any[]>('/ai/directives');
        if (res.success && res.data) setDirectives(res.data);
      } catch (err) {
        console.error('Failed to load directives:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        {t('ai.directivesDesc')}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {directives.map((dir) => (
          <div
            key={dir.id}
            className={`bg-[var(--color-bg-secondary)] rounded-lg border p-4 ${
              dir.is_active ? 'border-[var(--color-border)]' : 'border-red-200 dark:border-red-800 opacity-60'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-[var(--color-text)]">{dir.display_name}</h4>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                dir.is_active
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {dir.is_active ? t('ai.active') : t('ai.inactive')}
              </span>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(dir.keywords || []).map((kw: string, i: number) => (
                <span key={i} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded text-xs">
                  {kw}
                </span>
              ))}
            </div>
            <p className="text-xs text-[var(--color-text-secondary)] line-clamp-3">{dir.instruction}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────

function StatCard({ label, value, icon, color }: { label: string; value: any; icon: React.ReactNode; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-500 bg-green-50 dark:bg-green-900/20',
    red: 'text-red-500 bg-red-50 dark:bg-red-900/20',
    yellow: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    purple: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
    cyan: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
    emerald: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    orange: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
    gray: 'text-gray-500 bg-gray-50 dark:bg-gray-900/20',
  };

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded ${colorMap[color] || colorMap.gray}`}>{icon}</div>
        <span className="text-xs text-[var(--color-text-tertiary)]">{label}</span>
      </div>
      <div className="text-lg font-bold text-[var(--color-text)]">{value}</div>
    </div>
  );
}

function DraftDetailModal({ draft, onClose }: { draft: any; onClose: () => void }) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('ai.draftDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-4">
            {draft.product_image ? (
              <img src={draft.product_image} alt="" className="w-20 h-20 rounded-lg object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                <Package size={24} className="text-[var(--color-text-tertiary)]" />
              </div>
            )}
            <div>
              <p className="font-medium text-[var(--color-text)]">{draft.title_seo || '—'}</p>
              <div className="flex items-center gap-2 mt-1">
                {draft.asin && <EntityLink type="asin" value={draft.asin} />}
                {draft.brand && <span className="text-xs text-[var(--color-text-secondary)]">{draft.brand}</span>}
              </div>
              <div className="flex gap-2 mt-1">
                <StatusBadge status={DRAFT_STATUS_MAP[draft.status] || 'unknown'} label={draft.status} />
                {draft.needs_review && (
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded text-xs">{t('ai.needsReview')}</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label={t('ai.category')} value={draft.ml_category_name || '—'} />
            <DetailRow label={t('ai.score')} value={
              draft.validation_score != null
                ? <span className={`font-bold ${draft.validation_score >= 80 ? 'text-green-500' : draft.validation_score >= 60 ? 'text-yellow-500' : 'text-red-500'}`}>{draft.validation_score}/100</span>
                : '—'
            } />
            <DetailRow label={t('common.price')} value={canMoney && draft.price ? `${draft.currency_id || 'USD'} ${Number(draft.price).toFixed(2)}` : '—'} />
            <DetailRow label="Amazon" value={canMoney && draft.amazon_price ? `$${Number(draft.amazon_price).toFixed(2)}` : '—'} />
            <DetailRow label="Markup" value={canMoney && draft.markup_type ? `${draft.markup_type}: ${draft.markup_value}` : '—'} />
            <DetailRow label="Site" value={draft.site || '—'} />
            {draft.ml_item_id && <DetailRow label="ML Item ID" value={draft.ml_item_id} />}
            <DetailRow label={t('common.date')} value={draft.created_at ? new Date(draft.created_at).toLocaleString() : '—'} />
          </div>

          {draft.review_reasons && draft.review_reasons.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <h4 className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-1">{t('ai.reviewReasons')}</h4>
              <ul className="text-xs text-yellow-600 dark:text-yellow-300 space-y-0.5">
                {draft.review_reasons.map((r: string, i: number) => (
                  <li key={i}>• {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[var(--color-text-tertiary)] text-xs">{label}</span>
      <div className="text-[var(--color-text)] text-sm">{value}</div>
    </div>
  );
}
